import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { translate } from '@jsverse/transloco';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, map, tap, catchError, take } from 'rxjs/operators';
import { DeviceServiceService } from '../../devices/services/service.service';
import { ICreateClientServiceRequest, ICreateServiceRequest, IService, IServiceDetailsResponse, IUpdateClientServiceRequest, IUpdateServiceRequest, IUpdateServiceStatusRequest } from '../../devices/models/service';
import { AppNotificationService } from '../../shared/services/notification.service';
import { DeviceService } from '../../devices/services/device.service';
import { DeviceStore } from './device-store';

export interface DeviceState {
  deviceServices: IService[];
  openService: boolean;
  loading: boolean;
}

@Injectable()
export class DeviceServiceStore extends ComponentStore<DeviceState> {

  // Initial state
  constructor(
    private deviceServiceService: DeviceServiceService,
    private deviceStore: DeviceStore,
    private appnotificationService: AppNotificationService) {
      super({ 
        deviceServices: [],
        openService: false,
        loading: false
      });
  }

  // Selector for the list of services
  readonly deviceServices$: Observable<IService[]> = this.select(state => state.deviceServices);
  readonly openService$: Observable<boolean> = this.select(state => state.openService);
  readonly loading$ = this.select((state) => state.loading);


  // Effect to load services by device
  readonly getServicesByDevice = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(deviceId =>
        this.deviceServiceService.getServicesByDevice({pageNumber: 0, pageSize: 25, deviceId: deviceId}).pipe(
          tap(deviceServices => {
            const sortedServices = deviceServices.data.sort((a, b) => {
              // Primary sort by executionDate: null values should come first
              const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
              const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
              
              // If execution dates are different, prioritize null dates (they come first)
              if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                return -1;  // a should come before b
              }
              if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                return 1;   // b should come before a
              }
              
              // If both are the same (both null or both not null), sort by executionDate
              if (executionDateA !== executionDateB) {
                return executionDateB - executionDateA;  // Descending order
              }
            
              // Secondary sort by planDate if executionDate is the same (both null or both present)
              const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
              const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
              
              return planDateA - planDateB;  // Ascending order
            });
            
            const hasNullValue = sortedServices.some(service => service.dateExecuted === null);

            if (hasNullValue) {
              this.setOpenServiceTrue();
            }

            this.patchState(state => ({
              deviceServices: [...state.deviceServices, ...sortedServices],
              loading: false
            }));
          })
        )
      ),
      take(1)
    )
  );


  // Method to add a service to store
  readonly addService = this.effect<ICreateServiceRequest>(service$ =>
    service$.pipe(
      switchMap(service =>
        this.deviceServiceService.createDeviceService(service).pipe(
          tap(newService => {

            if (newService) {
              // Check if the new service has a null executionDate
              if (newService.dateExecuted === null) {
                this.setOpenServiceTrue();
              }

              this.appnotificationService.notification$.next({ message:translate('service.createSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('service.createFailed'), style: 'error' });
            }
  
            // Update the state with the new service and apply sorting
            this.patchState(state => ({
              deviceServices: [...state.deviceServices, newService].sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              })
            }));
          })
        )
      )
    )
  );
  

  // Method to add a client service to store
  readonly addClientService = this.effect<ICreateClientServiceRequest>(service$ =>
    service$.pipe(
      switchMap(service =>
        this.deviceServiceService.createClientService(service).pipe(
          tap(newService => {

            if (newService) {
              // Check if the new service has a null executionDate
              if (newService.dateExecuted === null) {
                this.setOpenServiceTrue();
              }

              this.appnotificationService.notification$.next({ message:translate('service.createSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('service.createFailed'), style: 'error' });
            }
  
            // Update the state with the new service and apply sorting
            this.patchState(state => ({
              deviceServices: [...state.deviceServices, newService].sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              })
            }));
          })
        )
      )
    )
  );
  

  // Effect to update a service by its ID
  readonly updateServiceById = this.effect<IUpdateServiceRequest>(service$ =>
    service$.pipe(
      switchMap(service =>
        this.deviceServiceService.updateDeviceService(service).pipe(
          tap(updatedService => {  
            // Update the service in the state and apply sorting
            this.patchState(state => {
              // Replace the old service with the updated one
              const updatedDeviceServices = state.deviceServices.map(n =>
                n.id === updatedService.id
                  ? { ...n, ...updatedService } // Merge updated fields
                  : n
              );

              // Check if any remaining services have null dateExecuted
              const hasOpenService = updatedDeviceServices.some(service => service.dateExecuted === null);

              // Optionally set openService to true if there's any service with null dateExecuted
              if (hasOpenService) {
                this.setOpenServiceTrue();
              } else {
                this.setOpenServiceFalse();
              }
  
              // Sort the updated list of services
              const sortedServices = updatedDeviceServices.sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              });
  
              return {
                deviceServices: sortedServices
              };
            });

            if (updatedService) {
              this.appnotificationService.notification$.next({ message:translate('service.updateSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('service.updateFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );


    // Effect to update a service by its ID
  readonly updateClientServiceById = this.effect<IUpdateClientServiceRequest>(service$ =>
    service$.pipe(
      switchMap(service =>
        this.deviceServiceService.updateClientDeviceService(service).pipe(
          tap(updatedService => {  
            // Update the service in the state and apply sorting
            this.patchState(state => {
              // Replace the old service with the updated one
              const updatedDeviceServices = state.deviceServices.map(n =>
                n.id === updatedService.id
                  ? { ...n, ...updatedService } // Merge updated fields
                  : n
              );

              // Check if any remaining services have null dateExecuted
              const hasOpenService = updatedDeviceServices.some(service => service.dateExecuted === null);

              // Optionally set openService to true if there's any service with null dateExecuted
              if (hasOpenService) {
                this.setOpenServiceTrue();
              } else {
                this.setOpenServiceFalse();
              }
  
              // Sort the updated list of services
              const sortedServices = updatedDeviceServices.sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              });
  
              return {
                deviceServices: sortedServices
              };
            });

            if (updatedService) {
              this.appnotificationService.notification$.next({ message:translate('service.updateSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('service.updateFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );
  
  

  // Method to remove a device service from store by its ID
  readonly removeServiceById = this.effect<string>(serviceId$ =>
    serviceId$.pipe(
      switchMap(serviceId =>
        this.deviceServiceService.deleteDeviceService(serviceId).pipe(
          tap(() => {
            this.patchState(state => {
              // Remove the service with the specified ID
              const updatedDeviceServices = state.deviceServices.filter(service => service.id !== serviceId);

              // Check if any remaining services have null dateExecuted
              const hasOpenService = updatedDeviceServices.some(service => service.dateExecuted === null);

              // Optionally set openService to true if there's any service with null dateExecuted
              if (hasOpenService) {
                this.setOpenServiceTrue();
              } else {
                this.setOpenServiceFalse();
              }

              // Sort the updated list of services
              const sortedServices = updatedDeviceServices.sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              });
  
              return {
                deviceServices: sortedServices
              };
                            
            });

            this.appnotificationService.notification$.next({ message:translate('service.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            return EMPTY; 
          })
        )
      )
    )
  );


  // Method to remove a device service from store by its ID
  readonly removeClientServiceById = this.effect<string>(serviceId$ =>
    serviceId$.pipe(
      switchMap(serviceId =>
        this.deviceServiceService.deleteClientService(serviceId).pipe(
          tap(() => {
            this.patchState(state => {
              // Remove the service with the specified ID
              const updatedDeviceServices = state.deviceServices.filter(service => service.id !== serviceId);

              // Check if any remaining services have null dateExecuted
              const hasOpenService = updatedDeviceServices.some(service => service.dateExecuted === null);

              // Optionally set openService to true if there's any service with null dateExecuted
              if (hasOpenService) {
                this.setOpenServiceTrue();
              } else {
                this.setOpenServiceFalse();
              }

              // Sort the updated list of services
              const sortedServices = updatedDeviceServices.sort((a, b) => {
                // Primary sort by executionDate: null values should come first
                const executionDateA = a.dateExecuted ? new Date(a.dateExecuted).getTime() : -Infinity;
                const executionDateB = b.dateExecuted ? new Date(b.dateExecuted).getTime() : -Infinity;
                
                // If execution dates are different, prioritize null dates (they come first)
                if (executionDateA === -Infinity && executionDateB !== -Infinity) {
                  return -1;  // a should come before b
                }
                if (executionDateA !== -Infinity && executionDateB === -Infinity) {
                  return 1;   // b should come before a
                }
                
                // If both are the same (both null or both not null), sort by executionDate
                if (executionDateA !== executionDateB) {
                  return executionDateB - executionDateA;  // Descending order
                }
              
                // Secondary sort by planDate if executionDate is the same (both null or both present)
                const planDateA = a.dateScheduled ? new Date(a.dateScheduled).getTime() : -Infinity;
                const planDateB = b.dateScheduled ? new Date(b.dateScheduled).getTime() : -Infinity;
                
                return planDateA - planDateB;  // Ascending order
              });
  
              return {
                deviceServices: sortedServices
              };
                            
            });

            this.appnotificationService.notification$.next({ message:translate('service.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            return EMPTY; 
          })
        )
      )
    )
  );
  

  // Function to set openService to true
  setOpenServiceTrue() {
    this.patchState(state => ({
      openService: true
    }));
  }

  // Function to set openService to false
  setOpenServiceFalse() {
    this.patchState(state => ({
      openService: false
    }));
  }

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
